import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiJson } from '../api';
import { Shell } from '../components/Shell';
import './StudyPage.css';
import './LandingPage.css';

type Summary = {
  bookmarks: Array<{ slug: string; title: string; summary: string | null; created_at: number }>;
  notes: Array<{
    id: string;
    slug: string;
    title: string;
    body: string;
    updated_at: number;
    micro_module_id?: string | null;
    micro_section_title?: string | null;
  }>;
  questions: Array<{ id: string; body: string; status: string; created_at: number; context_module_id: string | null }>;
  activeJourneys: Array<{
    slug: string;
    title: string;
    description: string | null;
    state_json: string;
    updated_at: number;
    step_count: number;
  }>;
  profile: {
    explicit: {
      worldviewBackground?: string;
      familiarityWithIslam?: string;
      motive?: string;
      reasoningPreference?: string;
      difficultyComfort?: string;
      explorationStyle?: 'open' | 'guided';
      preferredModuleDepth?: 'simple' | 'full' | 'study';
      adaptRecommendations?: boolean;
    };
  };
};

type LastVisit = { slug: string; title: string; at: number };

function readLast(key: string): LastVisit | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const v = JSON.parse(raw) as LastVisit;
    if (v && typeof v.slug === 'string' && typeof v.title === 'string') return v;
  } catch {
    /* ignore */
  }
  return null;
}

export function AccountPage() {
  const [data, setData] = useState<Summary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastModule, setLastModule] = useState<LastVisit | null>(null);
  const [prefBusy, setPrefBusy] = useState(false);

  useEffect(() => {
    setLastModule(readLast('wi_study_last'));
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiJson<Summary>('/api/account/summary');
        if (!cancelled) setData(res);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const savePrefs = async (patch: Summary['profile']['explicit']) => {
    setPrefBusy(true);
    try {
      const out = await apiJson<{ profile: Summary['profile'] }>('/api/me/profile', {
        method: 'PATCH',
        body: JSON.stringify({ explicit: patch }),
      });
      setData((d) =>
        d
          ? {
              ...d,
              profile: out.profile,
            }
          : d
      );
      try {
        if (patch.preferredModuleDepth) {
          localStorage.setItem('wi_depth_pref', patch.preferredModuleDepth);
        }
      } catch {
        /* ignore */
      }
    } catch {
      /* ignore */
    } finally {
      setPrefBusy(false);
    }
  };

  return (
    <Shell>
      <div className="landing-page wi-home-clean wi-home-v4 wi-home-nizam wi-study-home">
        <section className="wi-hero wi-band wi-band-hero" aria-labelledby="wi-home-hero-title">
          <div className="wi-section-inner wi-hero-inner">
            <div className="wi-hero-copy">
              <p className="wi-mk-kicker">Your space</p>
              <h1 id="wi-home-hero-title">My Space</h1>
              <p className="wi-hero-lede">A calm place for structured continuity: continue reading, revisit what you saved, and keep unresolved questions close.</p>

              {lastModule ? (
                <Link to={`/m/${lastModule.slug}`} className="wi-hero-cta-pill" aria-label="Continue">
                  Continue
                </Link>
              ) : (
                <Link to="/explore" className="wi-hero-cta-pill" aria-label="Start exploring">
                  Start exploring
                </Link>
              )}
            </div>
          </div>
        </section>

        <div className="landing-page-sections wi-home-sections">
          {error ? (
            <section className="wi-home-scene-block wi-study-scene wi-study-scene--error" aria-label="Error">
              <div className="wi-home-scene-veil" aria-hidden />
              <div className="wi-home-scene-layout">
                <div className="wi-home-scene-copy wi-home-scene-copy--upper wi-study-scene-copy">
                  <h2 className="wi-home-scene-title wi-home-scene-title--small">Could not load your space</h2>
                  <p className="wi-home-scene-lede">{error}</p>
                </div>
              </div>
            </section>
          ) : null}

          {!data && !error ? (
            <section className="wi-home-scene-block wi-study-scene wi-study-scene--loading" aria-label="Loading">
              <div className="wi-home-scene-veil" aria-hidden />
              <div className="wi-home-scene-layout">
                <div className="wi-home-scene-copy wi-home-scene-copy--upper wi-study-scene-copy">
                  <h2 className="wi-home-scene-title wi-home-scene-title--small">Loading your workspace</h2>
                  <p className="wi-home-scene-lede">Preparing bookmarks, journeys, notes, and preferences…</p>
                </div>
              </div>
            </section>
          ) : null}

          {data ? (
            <>
              <section className="wi-home-scene-block wi-study-scene wi-study-scene--saved" aria-label="Saved modules">
                <div className="wi-home-scene-veil" aria-hidden />
                <div className="wi-home-scene-layout">
                  <div className="wi-home-scene-copy wi-home-scene-copy--upper wi-study-scene-copy">
                    <h2 className="wi-home-scene-title wi-home-scene-title--small">Saved modules</h2>
                    {data.bookmarks.length ? (
                      <ul className="wi-study-scene-list">
                        {data.bookmarks.map((b) => (
                          <li key={b.slug}>
                            <Link to={`/m/${b.slug}`}>{b.title}</Link>
                            {b.summary ? (
                              <span className="wi-study-item-meta">
                                {b.summary.slice(0, 120)}{b.summary.length > 120 ? '…' : ''}
                              </span>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="wi-home-scene-lede">No saved modules yet. Save from modules when something resonates.</p>
                    )}
                  </div>
                </div>
              </section>

              <section className="wi-home-scene-block wi-study-scene wi-study-scene--journeys" aria-label="Active journeys">
                <div className="wi-home-scene-veil" aria-hidden />
                <div className="wi-home-scene-layout">
                  <div className="wi-home-scene-copy wi-home-scene-copy--upper wi-study-scene-copy">
                    <h2 className="wi-home-scene-title wi-home-scene-title--small">Active journeys</h2>
                    {data.activeJourneys?.length ? (
                      <ul className="wi-study-scene-list">
                        {data.activeJourneys.map((j) => {
                          let pos = 1;
                          try {
                            const st = JSON.parse(j.state_json || '{}') as { lastStepPosition?: number };
                            if (st.lastStepPosition) pos = st.lastStepPosition;
                          } catch {
                            /* ignore */
                          }
                          return (
                            <li key={j.slug}>
                              <Link to={`/journeys/${j.slug}`}>{j.title}</Link>
                              <span className="wi-study-item-meta">
                                Last at step {pos} of {j.step_count ?? '—'}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    ) : (
                      <p className="wi-home-scene-lede">No journey progress yet — open a journey and begin a step.</p>
                    )}
                    <Link to="/journeys" className="wi-study-muted-inline">
                      Browse all journeys →
                    </Link>
                  </div>
                </div>
              </section>

              <section className="wi-home-scene-block wi-study-scene wi-study-scene--prefs" aria-label="Preferences">
                <div className="wi-home-scene-veil" aria-hidden />
                <div className="wi-home-scene-layout">
                  <div className="wi-home-scene-copy wi-home-scene-copy--upper wi-study-scene-copy">
                    <h2 className="wi-home-scene-title wi-home-scene-title--small">Preferences</h2>
                    <p className="wi-home-scene-lede">Adjust how modules open and whether recommendations adapt.</p>
                    <div className="wi-study-prefs">
                      <div className="wi-study-prefs-grid" role="group" aria-label="Preference questions">
                      <label className="wi-study-field wi-study-field--select wi-study-pref-item">
                        <span className="wi-study-pref-item__title">Background</span>
                        <span className="wi-study-pref-item__hint">Helps orient comparisons and language.</span>
                        <select
                          disabled={prefBusy}
                          value={data.profile?.explicit?.worldviewBackground ?? ''}
                          onChange={(e) => savePrefs({ worldviewBackground: e.target.value || undefined })}
                        >
                          <option value="">Prefer not to say</option>
                          <option value="muslim">Muslim</option>
                          <option value="christian">Christian</option>
                          <option value="atheist">Atheist / secular</option>
                          <option value="agnostic">Agnostic</option>
                          <option value="jewish">Jewish</option>
                          <option value="hindu">Hindu</option>
                          <option value="spiritual_not_religious">Spiritual, not religious</option>
                          <option value="other">Other</option>
                        </select>
                      </label>

                      <label className="wi-study-field wi-study-field--select wi-study-pref-item">
                        <span className="wi-study-pref-item__title">Familiarity with Islam</span>
                        <span className="wi-study-pref-item__hint">Tunes whether guidance starts foundational or advanced.</span>
                        <select
                          disabled={prefBusy}
                          value={data.profile?.explicit?.familiarityWithIslam ?? ''}
                          onChange={(e) => savePrefs({ familiarityWithIslam: e.target.value || undefined })}
                        >
                          <option value="">Prefer not to say</option>
                          <option value="totally_new">Totally new</option>
                          <option value="know_basics">Know basics</option>
                          <option value="studied_little">Studied a little</option>
                          <option value="researching_deeply">Researching deeply</option>
                        </select>
                      </label>

                      <label className="wi-study-field wi-study-field--select wi-study-pref-item">
                        <span className="wi-study-pref-item__title">What matters most right now?</span>
                        <span className="wi-study-pref-item__hint">Prioritizes what should appear first.</span>
                        <select
                          disabled={prefBusy}
                          value={data.profile?.explicit?.motive ?? ''}
                          onChange={(e) => savePrefs({ motive: e.target.value || undefined })}
                        >
                          <option value="">Prefer not to say</option>
                          <option value="curiosity">Curiosity</option>
                          <option value="comparison">Comparing beliefs</option>
                          <option value="concern">Concern or tension</option>
                          <option value="doubt">Doubt</option>
                          <option value="spiritual_search">Spiritual search</option>
                          <option value="specific_issue">A specific issue</option>
                        </select>
                      </label>

                      <label className="wi-study-field wi-study-field--select wi-study-pref-item">
                        <span className="wi-study-pref-item__title">Comfort with depth</span>
                        <span className="wi-study-pref-item__hint">Balances overview vs direct deeper analysis.</span>
                        <select
                          disabled={prefBusy}
                          value={data.profile?.explicit?.difficultyComfort ?? 'overview_first'}
                          onChange={(e) => savePrefs({ difficultyComfort: e.target.value || undefined })}
                        >
                          <option value="overview_first">Overview first</option>
                          <option value="balanced_depth">Balanced depth</option>
                          <option value="direct_deep_dive">Direct, deeper dives</option>
                        </select>
                      </label>

                      <label className="wi-study-field wi-study-field--select wi-study-pref-item">
                        <span className="wi-study-pref-item__title">Exploration style</span>
                        <span className="wi-study-pref-item__hint">Choose open browsing or guided paths first.</span>
                        <select
                          disabled={prefBusy}
                          value={data.profile?.explicit?.explorationStyle ?? 'open'}
                          onChange={(e) => savePrefs({ explorationStyle: e.target.value as 'open' | 'guided' })}
                        >
                          <option value="open">Open exploration</option>
                          <option value="guided">Guided paths first</option>
                        </select>
                      </label>

                      <label className="wi-study-field wi-study-field--select wi-study-pref-item">
                        <span className="wi-study-pref-item__title">Default reading depth</span>
                        <span className="wi-study-pref-item__hint">Choose how detailed modules open by default.</span>
                        <select
                          disabled={prefBusy}
                          value={data.profile?.explicit?.preferredModuleDepth ?? 'full'}
                          onChange={(e) =>
                            savePrefs({
                              preferredModuleDepth: e.target.value as 'simple' | 'full' | 'study',
                            })
                          }
                        >
                          <option value="simple">Short answer</option>
                          <option value="full">Full explanation</option>
                          <option value="study">Deeper analysis</option>
                        </select>
                      </label>
                      </div>

                      <label className="wi-study-field wi-study-field--check wi-study-pref-item">
                        <span className="wi-study-pref-item__title">Adaptive recommendations</span>
                        <span className="wi-study-pref-item__hint">Use your saved items and activity to tune what appears next.</span>
                        <span className="wi-study-check-row">
                          <input
                            type="checkbox"
                            disabled={prefBusy}
                            checked={data.profile?.explicit?.adaptRecommendations !== false}
                            onChange={(e) => savePrefs({ adaptRecommendations: e.target.checked })}
                          />
                          <span>Keep this on</span>
                        </span>
                      </label>

                      <div className="wi-study-prefs__footer">
                        {prefBusy ? <span className="wi-study-prefs__saving">Saving…</span> : null}
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <section className="wi-home-scene-block wi-study-scene wi-study-scene--questions" aria-label="Questions">
                <div className="wi-home-scene-veil" aria-hidden />
                <div className="wi-home-scene-layout">
                  <div className="wi-home-scene-copy wi-home-scene-copy--upper wi-study-scene-copy">
                    <h2 className="wi-home-scene-title wi-home-scene-title--small">Questions you’re holding</h2>
                    {data.questions.length ? (
                      <ul className="wi-study-scene-list">
                        {data.questions.map((q) => (
                          <li key={q.id}>
                            <p style={{ margin: 0 }}>{q.body}</p>
                            <span className="wi-study-item-meta">{q.status}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="wi-home-scene-lede">No questions logged yet. Your inquiry trail will appear here.</p>
                    )}
                  </div>
                </div>
              </section>

              <section className="wi-home-scene-block wi-study-scene wi-study-scene--notes" aria-label="Notes">
                <div className="wi-home-scene-veil" aria-hidden />
                <div className="wi-home-scene-layout">
                  <div className="wi-home-scene-copy wi-home-scene-copy--upper wi-study-scene-copy">
                    <h2 className="wi-home-scene-title wi-home-scene-title--small">Notes</h2>
                    {data.notes.length ? (
                      <ul className="wi-study-scene-list">
                        {data.notes.map((n) => (
                          <li key={n.id}>
                            <Link to={`/m/${n.slug}`}>{n.title}</Link>
                            {n.micro_section_title ? (
                              <span className="wi-study-item-meta">Section: {n.micro_section_title}</span>
                            ) : null}
                            <span className="wi-study-item-meta">
                              {n.body.slice(0, 120)}
                              {n.body.length > 120 ? '…' : ''}
                            </span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="wi-home-scene-lede">Optional reflections tied to modules will show here — kept light on purpose.</p>
                    )}
                  </div>
                </div>
              </section>
            </>
          ) : null}
        </div>
      </div>
    </Shell>
  );
}
